import { PreloadingStrategy, Route } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Observable, of, defer } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';

export class SimpleLoadingStrategy implements PreloadingStrategy {
  private readonly browserIdle$ = defer(() => new Observable<void>(subscriber => {
    if (typeof window === 'undefined') {
      subscriber.next();
      subscriber.complete();
      return;
    }

    let delayTimer: ReturnType<typeof setTimeout> | undefined;
    let idleHandle: number | undefined;
    const requestIdle = (window as any).requestIdleCallback as
      ((callback: () => void, options?: { timeout: number }) => number) | undefined;
    const cancelIdle = (window as any).cancelIdleCallback as
      ((handle: number) => void) | undefined;

    const completeWhenIdle = () => {
      // Keep route downloads away from WebKit's first Angular paint. The idle
      // timeout still guarantees that preload routes eventually become warm.
      delayTimer = setTimeout(() => {
        if (requestIdle) {
          idleHandle = requestIdle(() => {
            subscriber.next();
            subscriber.complete();
          }, { timeout: 8000 });
          return;
        }
        subscriber.next();
        subscriber.complete();
      }, 1500);
    };

    if (document.readyState === 'complete') completeWhenIdle();
    else window.addEventListener('load', completeWhenIdle, { once: true });

    return () => {
      window.removeEventListener('load', completeWhenIdle);
      if (delayTimer) clearTimeout(delayTimer);
      if (idleHandle !== undefined && cancelIdle) cancelIdle(idleHandle);
    };
  })).pipe(shareReplay(1));

  preload(route: Route, load: Function): Observable<any> {
    // On the web, let the browser download a route chunk only when the user
    // visits that route. Keep the existing warm-up behavior in native apps.
    if (!Capacitor.isNativePlatform()) {
      return of(null);
    }

    if (route.data && route.data.preload) {
      return this.browserIdle$.pipe(switchMap(() => load()));
    }
    return of(null);
  }
}
